import { downloadFile } from 'src/helpers/file-helper';
import { getEventLog, getStatistics } from 'src/services/api/api-client/ExportLogClient';
import {
  FileTypeVariantsType,
  PatientReportVariantsType,
  ReportScopeEnum,
  StudyReportVariantsType,
} from './reportExporting';
import { IPatientListDto } from 'src/services/api/api-client';
import { useStaffProfile, useStudy } from 'src/helpers/hooks/useStudy';
import { generateRecordsReport } from '../report/records/generateRecordsReport';
import { generateEcrfReport } from '../report/ecrf/generateEcrfReport';
import { generateSurveyReport } from '../report/surveys/generateSurveyReport';
import { logger } from 'src/application/logging/logging';

export type ExportReportArgs = {
  scope: ReportScopeEnum;
  fileType: FileTypeVariantsType;
  surveyTypes: string[];
  formTypes: string[];
  includeAllVersions: boolean;
  withMissedSurveys: boolean;
  signedPdf: boolean;
  logType: PatientReportVariantsType | StudyReportVariantsType;
  patient?: IPatientListDto;
};

export const useExportReportHandlers = () => {
  const study = useStudy();
  const { profile } = useStaffProfile();

  const exportReport = async ({
    patient,
    logType,
    fileType,
    surveyTypes,
    formTypes,
    includeAllVersions,
    signedPdf,
    scope,
    withMissedSurveys,
  }: ExportReportArgs) => {
    if (!study) throw new Error('Study data not provided');
    if (!profile) throw new Error('Profile data not provided');
    if (scope === ReportScopeEnum.Patient && !patient) throw new Error('Patient data not provided');

    if (logType === 'Auditlog') {
      const fileResponse = await getEventLog(study.id, patient?.uniqueId);
      if (!fileResponse) return;

      downloadFile(fileResponse);
      return;
    }

    if (logType === 'Statistics' && fileType === 'Csv') {
      const fileResponse = await getStatistics(
        study.id,
        surveyTypes,
        formTypes,
        undefined,
        patient?.uniqueId,
        !includeAllVersions,
      );
      if (!fileResponse) return;

      downloadFile(fileResponse);
      return;
    }

    if (scope === ReportScopeEnum.Patient && logType === 'Statistics' && fileType === 'Pdf') {
      if (formTypes.length)
        await generateRecordsReport(
          {
            id: patient!.id,
            uniqueId: patient!.uniqueId,
            tags: patient!.tags,
            routeProgress: patient!.studyRouteProgress,
          },
          study,
          profile.fullName,
          formTypes,
          includeAllVersions,
          signedPdf,
        );

      if (surveyTypes.length)
        await generateSurveyReport(
          patient!,
          surveyTypes,
          {
            studyNumber: study.studyNumber,
            title: study?.title,
          },
          profile.fullName,
          withMissedSurveys,
          signedPdf,
        );

      logger().info(`PDF report generation completed for patient ${patient!.uniqueId} in study ${study.studyNumber}`);
      return;
    }

    if (scope === ReportScopeEnum.Patient && logType === 'eCRF') {
      await generateEcrfReport(
        study,
        {
          id: patient!.id,
          uniqueId: patient!.uniqueId,
          routeProgress: patient!.studyRouteProgress,
        },
        profile.fullName,
      );

      logger().info(`eCRF report generation completed for patient ${patient!.uniqueId} in study ${study.studyNumber}`);
      return;
    }
  };

  return exportReport;
};
